import { RULE_OPTIONS as ruleOptions } from "../../../constants/visualizationOptions";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import {
  Button,
  Grid,
  Box,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Menu,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  clearPlot,
  fetchPlot,
  saveVisualization,
  fetchSavedVisualization,
  setViewEditPlotDatToState,
  fetchFilteredBatchList,
} from "../../../redux/visualization/actions";

import CAMHeader from "../../../components/Header/CAMHeader";
import CAMDrawer from "../../../components/LeftSideBar/CAMDrawer";
import InvestigatorContainer from "../../../components/contsiners/InvestigatorContainer";
import Label from "../../../components/Label/Label";
import "react-datepicker/dist/react-datepicker.css";
import BatchFilterPanel from "./batchFIlters/BatchFilterPanel";
import "./VisualizationPage.scss";
import BatchPreview from "./batchFIlters/BatchLIstPreview";
import ControlChartConfigs from "./controlChart/ControlChartConfigs";
import ProcessCapabilityConfigs from "./processCapabilityChart/ProcessCapabilityConfigs";
import PlotComponent from "./plot/plot";

import { useLocation, useNavigate, useParams } from "react-router-dom";
import XBarChartConfigs from "./xBarChart/XBarChartConfigs";
import DropdownsBar from "../header/ProductSiteUnitSelector";
import TabsBar from "../header/TabsNav";
import BreadcrumbsBar from "../header/BreadcrumbNav";
import TableComponent from "./TableComponent.jsx";
import PPChartConfigs from "./ppChart/PPChartConfigs.jsx";
import SubPlots from "./subPlots/SubPlots.jsx";
import SaveChartDialog from "./SaveChartDialog";
import Histogram from "./histogram/Histogram.jsx";
import MainTable from "./MainTable.jsx";
import HeaderTabsBar from "./ChartSectionHeader.jsx";
import CreateChartsPanel from "./createCharts/CreateChartsPanel.jsx";
import VisualizationTableSubChartAndOtherData from "./VisualizationTableSubChartAndOtherData.jsx";
import ParameterFilterPanel from "./parameterFilters/ParameterFilter.jsx";
import { manageDataRoute } from "../../../routes/routeConstants.js";
import { setProductSitesUopFilters } from "../../../redux/shared/actions.js";
import { hasPermission } from "../../../utils/permissions.js";
import {
  PERMISSION_MODULES,
  VISUALIZATION_PERMISSIONS,
} from "../../../constants/permissions";

// -------------------- helpers (pure, top-level) --------------------
const camelToSnake = (str) => str.replace(/([A-Z])/g, "_$1").toLowerCase();
const isPlainObject = (obj) =>
  obj && typeof obj === "object" && obj.constructor === Object;

const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) return obj.map(toSnakeCase);
  if (isPlainObject(obj)) {
    return Object.keys(obj).reduce((acc, key) => {
      const snakeKey = camelToSnake(key);
      acc[snakeKey] = toSnakeCase(obj[key]);
      return acc;
    }, {});
  }
  return obj;
};

const toCamelCase = (obj) => {
  if (Array.isArray(obj)) return obj.map(toCamelCase);
  if (isPlainObject(obj)) {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
        letter.toUpperCase(),
      );
      acc[camelKey] = toCamelCase(obj[key]);
      return acc;
    }, {});
  }
  return obj;
};

// initialState kept outside to avoid recreation
const initialState = () => ({
  // selectedTab: "batchFilters",
  parameterState: {
    product: { id: "", name: "" },
    site: { id: "", name: "" },
    unitOfOperation: { id: "", name: "" },
    parameter: {},
    plotType: "",
  },

  batchState: {
    batchRangeOption: "by_latest_x",
    recentBatchCount: "20",
    recentDayCount: "",
    startDate: "",
    endDate: "",
    batchIdList: [],
  },
  controlChartConfigState: {
    specLimitType: "",
    lowerSpecLimit: "",
    upperSpecLimit: "",
    specificationTarget: "",
    showZoomScale: false,
    zoomYMin: "",
    zoomYMax: "",
    controlLimitType: "Sample Calculation",
    lowerControlLimit: "",
    upperControlLimit: "",
    movingAverage: false,
    plotAggregation: true,
    ruleset: "nelson",
    selectedRules: ["rule1"],
    controlLimitSetId: "",
    specLimitSetId: "",
    show_grid: false,
    show_target: false,
  },
  pccChartConfigState: {
    specLimitType: "User Preset",
    lowerSpecLimit: "",
    upperSpecLimit: "",
    specificationTarget: "",
    controlLimitType: "Sample Calculation",
    lowerControlLimit: "",
    upperControlLimit: "",
    controlLimitSetId: "",
    specLimitSetId: "",
    selectedCurves: ["norm"],
    show_target: false,
    show_grid: false,
  },
  xBarChartConfigState: {
    controlLimitType: "Sample Calculation",
    lowerControlLimit: "",
    upperControlLimit: "",
    selectedBatchId: "",
    controlLimitSetId: "",
    ruleset: "nelson",
    selectedRules: ["rule1"],
    show_grid: false,
  },
  ppChartConfigState: {
    selectedCurves: ["norm"],
    show_grid: false,
  },
});

const chartConfigComponentMap = {
  control_chart: { component: ControlChartConfigs, key: "controlChart" },
  process_capability: {
    component: ProcessCapabilityConfigs,
    key: "processCapability",
  },
  xbar_chart: { component: XBarChartConfigs, key: "xbarChart" },
  pp_chart: { component: PPChartConfigs, key: "ppChart" },
};

function useElementWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      setWidth(rect.width);
    });

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

const VisualizationPage = () => {
  const dispatch = useDispatch();
  const { chartType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    viz_id,
    mode,
    vizName,
    chartType: initialChartType,
    version_id,
  } = location.state || {};
  const typeOfMode = mode || "create";
  const canCreateChart = hasPermission(
    PERMISSION_MODULES.VISUALIZATION,
    VISUALIZATION_PERMISSIONS.CREATE_NEW_CHART,
  );
  const canEditChart = hasPermission(
    PERMISSION_MODULES.VISUALIZATION,
    VISUALIZATION_PERMISSIONS.EDIT_CHART,
  );
  const canDeleteChart = hasPermission(
    PERMISSION_MODULES.VISUALIZATION,
    VISUALIZATION_PERMISSIONS.DELETE_CHART,
  );
  const canSaveChart =
    typeOfMode === "create"
      ? canCreateChart
      : typeOfMode === "edit"
        ? canEditChart
        : false;

  // If we navigated back here from Manage Data (via the "Back to Chart"
  // action), this holds everything we snapshotted before leaving, so the
  // whole page can restore itself instead of re-initializing/re-fetching.
  const restoredState = location.state?.restoredChartsPageState || null;

  // Selectors with shallowEqual where appropriate to avoid re-renders

  const vizPlot = useSelector((s) => s.viz.plot || {}, shallowEqual);
  const { data, loader, error, message, success } = vizPlot;

  const filteredBatchList = useSelector(
    (s) => s.viz.filteredBatchList || {},
    shallowEqual,
  );
  const {
    data: batchData, // directly settin responseat below when api call
    loader: batchLoader,
    apiError: batchError,
    message: batchMessage,
    apiSuccess: batchSuccess,
  } = filteredBatchList;

  // const [anchorEl, setAnchorEl] = useState(null);
  // const openChartMenu = Boolean(anchorEl);

  // local UI state
  const [parsedData, setParsedData] = useState([]);
  const [subPlotsData, setSubPlotsData] = useState({});
  const [tableData, setTableData] = useState([]);
  const [violationTableData, setViolationTableData] = useState([]);
  const [titles, setTitles] = useState({});
  // const [state, setState] = useState(() => initialState());
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [previewData, setPreviewData] = useState(
    () => restoredState?.previewData ?? [],
  );
  const [showPreview, setShowPreview] = useState(
    () => restoredState?.showPreview ?? false,
  );
  // const [plotKey, setPlotKey] = useState(0);
  // const [configBatchList, setConfigBatchList] = useState([]);
  const [configSetIds, setConfigSetIds] = useState(
    () =>
      restoredState?.configSetIds ?? {
        control_limit_options: [],
        specification_limit_options: [],
      },
  );
  const [rawData, setRawData] = useState([]);
  // const [activeTab, setActiveTab] = useState("chart");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [histogramData, setHistogramData] = useState({});
  const [open, setOpen] = useState(false);
  const [disableAllFields, setDisableAllFields] = useState(
    () => restoredState?.disableAllFields ?? false,
  );
  const [saveSnackbar, setSaveSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const lastPreviewPayload = useRef(null);

  const [pageLoader, setpageLoader] = useState(false);

  // ------------------- New State ----------------//
  const [charts, setCharts] = useState(() => {
    if (restoredState?.charts) return restoredState.charts;

    return typeOfMode === "create"
      ? [
          {
            id: Date.now().toString(),
            name: `Chart 1`,
            selected: false,
            isEditing: true,
            // configState: {
            //   batchState: {},
            //   chartConfig: {},
            // },
            batchState: { ...initialState().batchState },
            parameterState: {
              ...initialState().parameterState,
              plotType: initialChartType,
            },
            // chartConfig: {},
          },
        ]
      : [];
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [activeId, setActiveId] = useState(
    () => restoredState?.activeId ?? null,
  );

  const [selectedTab, setSelectedTab] = useState(
    () => restoredState?.selectedTab ?? "parameter",
  );

  const activeChart = charts?.find((c) => c?.id === activeId);
  const batchState = activeChart?.batchState || {};
  const parameterState = activeChart?.parameterState || {};
  const chartTypeLocal = activeChart?.parameterState?.plotType || "";
  const configBatchList = activeChart?.configBatchList || [];
  // const configSetIds = activeChart?.configSetIds || {};

  const [showConfigPanel, setShowConfigPanel] = useState(
    () => restoredState?.showConfigPanel ?? false,
  );
  const [leftRef, leftWidth] = useElementWidth();
  const { product, site, unitOfOperation } = parameterState;

  const [showSummaryTable, setShowSummaryTable] = useState(
    () => restoredState?.showSummaryTable ?? true,
  );
  const [showViolationTable, setShowViolationTable] = useState(
    () => restoredState?.showViolationTable ?? true,
  );

  const chartConfigMap = useMemo(
    () => ({
      control_chart: {
        key: "controlChartConfigState",
        value: activeChart?.controlChartConfigState,
      },
      process_capability: {
        key: "pccChartConfigState",
        value: activeChart?.pccChartConfigState,
      },
      xbar_chart: {
        key: "xBarChartConfigState",
        value: activeChart?.xBarChartConfigState,
      },
      pp_chart: {
        key: "ppChartConfigState",
        value: activeChart?.ppChartConfigState,
      },
    }),
    [
      activeChart?.controlChartConfigState,
      activeChart?.pccChartConfigState,
      activeChart?.xBarChartConfigState,
      activeChart?.ppChartConfigState,
    ],
  );

  // const activeChartState = activeChart || {};
  // const config = activeChart?.chartConfig || {};

  // Extract the actual Config component and current chart config key/value
  const { component: ConfigComponent } =
    chartConfigComponentMap[chartTypeLocal] || {};

  const { key: currentChartKey, value: currentChartConfigState } =
    chartConfigMap[chartTypeLocal] || {};

  // -------------------- Effects --------------------
  useEffect(() => {
    if (!batchLoader) {
      if (batchError) {
        setSnackbar({
          open: true,
          severity: "error",
          message:
            batchMessage || "Something went wrong while fetching batches.",
        });
        setPreviewData([]);
      } else if (batchSuccess) {
        setSnackbar({
          open: true,
          severity: "success",
          message: batchMessage || "Batches loaded successfully!",
        });
      }
    }
  }, [batchLoader, batchError, batchSuccess, batchMessage]);

  // useEffect(() => {
  //   if (typeOfMode === "create") {
  //     setState((prev) => {
  //       const base = initialState();
  //       return {
  //         ...base,
  //         batchState: { ...base.batchState, plotType: chartType },
  //       };
  //     });
  //   }
  // }, [product.id, site.id, unitOfOperation.id]);

  // Fetch saved viz when in view/edit mode

  const fetchData = async (mode, type) => {
    setpageLoader(true);
    try {
      const payload = {
        // drug_product_id: product.id,
        // site_id: site.id,
        // unit_op_id: unitOfOperation.id,
        version_id: version_id,
        viz_id: viz_id,
        author_id: localStorage.getItem("userid"),
        refresh_flag: mode === "refresh" ? true : false,
        ...(mode === "refresh" && { type: type }),
      };
      const response = await dispatch(fetchSavedVisualization(payload));
      if (response) {
        const safeDate = (value) => {
          if (!value) return null;
          const date = new Date(value);
          return isNaN(date.getTime()) ? null : date;
        };

        const toFillCharts = [];

        response?.charts?.map((chart) => {
          const batchStateToCamelCase = chart.payload_data.batchs_selection
            ? toCamelCase(chart.payload_data.batchs_selection)
            : activeChart.batchState;

          const chartKey =
            chartConfigMap[chart.payload_data.batchs_selection.plot_type]
              ?.key || "";

          const toFillData = {
            // ...prev,

            id: chart.id || crypto.randomUUID(),
            name: chart.chart_name,
            selected: true,
            chart_uuid: chart?.chart_uuid,
            // site_id: chart.site_id || "SITE_001",
            // drug_product_id: chart.drug_product_id || "PROD_1",
            // unit_op_id: chart.unit_op_id || "UOP_1",

            batchState: {
              ...batchStateToCamelCase,
              startDate: safeDate(batchStateToCamelCase?.startDate),
              endDate: safeDate(batchStateToCamelCase?.endDate),
            },

            [chartKey]: toCamelCase(chart.payload_data.viz_config),

            parameterState: {
              product: chart.product,
              site: chart.site,
              unitOfOperation: chart.unitOfOperation,
              parameter: toCamelCase(
                chart.payload_data.batchs_selection.parameter,
              ),
              plotType: chart.payload_data.batchs_selection.plot_type,
            },
          };
          toFillCharts.push(toFillData);
        });
        setCharts([...toFillCharts]);
        setActiveId(toFillCharts[0]?.id || null);

        // if (Array.isArray(batchData))
        // setPreviewData(
        //   response.payload_data.batchs_selection.batch_id_list.map(
        //     (batch) => ({ batch_id: batch, isSelected: true }),
        //   ),
        // );

        // if (viz_data) dispatch(setViewEditPlotDatToState(viz_data));
        // setDisableAllFields(typeOfMode === "view");
        // response?.charts?.map((chart) => {
        //   chart.id = chart.id || crypto.randomUUID();
        //   updateChartData(chart.id, chart.viz_data);
        // });

        toFillCharts.forEach((chart, index) => {
          updateChartData(chart.id, response.charts[index].viz_data);
        });
      }
      setpageLoader(false);
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to fetch saved visualization",
      });
      setpageLoader(false);
    }
  };

  useEffect(() => {
    // Skip the fetch entirely when we've just returned from Manage Data —
    // charts/activeId/etc. were already restored from the snapshot above.
    if (typeOfMode !== "create" && !restoredState) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeOfMode]);
  // useEffect(() => {
  //   if (typeOfMode === "create" || typeOfMode ==="edit") {
  //     if (state.batchState.parameter) {
  //       setState((prev) => {
  //         const base = initialState();
  //         return {
  //           ...base,
  //           batchState: {
  //             ...base.batchState,
  //             plotType: prev.batchState.plotType,
  //             parameter: prev.batchState.parameter,
  //           },
  //         };
  //       });
  //     }
  //   }
  // }, [state.batchState.parameter]);

  // useEffect(() => {
  //   if (typeOfMode === "create" || typeOfMode ==="edit") {
  //     if (chartType) {
  //       setState((prev) => {
  //         const base = initialState();
  //         return {
  //           ...base,
  //           batchState: {
  //             ...base.batchState,
  //             plotType: chartType,
  //             parameter: prev.batchState.parameter,
  //           },
  //         });
  //       });
  //     }
  //   }
  // }, [chartType]);

  // useEffect(() => {
  //   if (data) {
  //     const parsedPlot =
  //       typeof data?.plot_data === "string" && data.plot_data.trim() !== ""
  //         ? JSON.parse(data.plot_data)
  //         : {};
  //     const subPlots = data?.subplots || {};
  //     const histoData = data?.histogram || {};

  //     setParsedData(parsedPlot);
  //     setSubPlotsData(subPlots);
  //     setHistogramData(histoData);
  //     setTableData(data?.summary_table || []);
  //     setViolationTableData(data?.voilation_table || []);
  //     setTitles(data?.titles || {});

  //     if (data?.info) {
  //       setConfigBatchList(data?.info?.available_batch_ids || []);
  //       setConfigSetIds({
  //         control_limit_options: data?.info?.control_limit_options || [],
  //         specification_limit_options:
  //           data?.info?.specification_limit_options || [],
  //       });
  //       setRawData(data?.info?.raw_data || []);
  //     }

  //     if (chartTypeLocal === "xbar_chart") {
  //       setState((prevState) => ({
  //         ...prevState,
  //         xBarChartConfigState: {
  //           ...prevState.xBarChartConfigState,
  //           selectedBatchId: data?.info?.current_batch_id,
  //         },
  //       }));
  //       setCharts(
  //         charts.map((chart) => {
  //           if (chart?.id !== activeId) return chart;

  //           return {
  //             ...chart,
  //             xBarChartConfigState: {
  //               ...chart?.xBarChartConfigState,
  //               selectedBatchId: data?.info?.current_batch_id,
  //             },
  //           };
  //         }),
  //       );
  //     }
  //   }
  // }, [data]);

  useEffect(() => {
    return () => {
      dispatch(clearPlot());
    };
  }, [dispatch]);

  useEffect(() => {
    if (typeOfMode === "create") {
      if (parameterState?.plotType) {
        navigate(`/analytics/visualization/charts/${parameterState.plotType}`, {
          replace: true,
        });

        // if (batchState.plotType !== "pp_chart" && activeTab === "subplots") {
        //   setActiveTab("chart");
        // }
      }
    }
  }, [parameterState?.plotType, typeOfMode, navigate]);

  useEffect(() => {
    if (
      typeOfMode === "create" &&
      charts &&
      charts?.length === 1 &&
      !restoredState
    ) {
      setActiveId(charts[0]?.id || null);
      const targetKey =
        chartConfigMap[charts[0]?.parameterState?.plotType]?.key;

      if (charts[0]?.parameterState?.plotType) {
        setCharts((prevCharts) => {
          // return {
          //   ...prevCharts,
          //   [targetKey]: initialState()[targetKey],
          // };
          const updatedCharts = [...prevCharts];
          updatedCharts[0][targetKey] = initialState()[targetKey];
          return updatedCharts;
        });
        setShowConfigPanel(true);
      }
    }
  }, []);

  // useEffect(() => {
  // if (!Array.isArray(batchData)) {
  //   setPreviewData([]);
  //   return;
  // }
  // const selectedIds = state.batchState.batchIdList || [];
  // if (previewData.length === 0) {
  // Only initialize previewData if empty
  // if (typeOfMode === "create") {
  // setPreviewData(batchData.map((b) => ({ ...b, isSelected: true })));
  // }
  //  else
  // if (typeOfMode === "view") {
  //   setPreviewData(
  //     batchData
  //       .filter((batch) => selectedIds.includes(batch.batch_id))
  //       .map((batch) => ({ ...batch, isSelected: true }))
  //   );
  // } else if (typeOfMode === "edit") {
  //   setPreviewData(
  //     batchData.map((batch) => ({
  //       ...batch,
  //       isSelected: selectedIds.includes(batch.batch_id),
  //     }))
  //   );
  // }
  // }
  // }, [batchData, state.batchState.batchIdList, typeOfMode]);

  // -------------------- callbacks (memoized) --------------------
  // const updateState = useCallback(
  //   (chartId, groupKey, key, value) => {
  //     if (typeOfMode === "create" || typeOfMode === "edit") {
  //       setState((prev) => {
  //         const base = initialState();

  //         if (groupKey === "batchState" && key === "parameter") {
  //           return {
  //             ...prev,
  //             batchState: {
  //               ...prev.batchState,
  //               [key]: value,
  //             },
  //             // controlChartConfigState: base.controlChartConfigState,
  //             // pccChartConfigState: base.pccChartConfigState,
  //             // xBarChartConfigState: base.xBarChartConfigState,
  //             // ppChartConfigState: base.ppChartConfigState,
  //           };
  //         }

  //         if (groupKey === "batchState" && key === "plotType") {
  //           const targetKey = chartConfigMap[value]?.key;

  //           return {
  //             ...prev,
  //             batchState: {
  //               ...prev.batchState,
  //               plotType: value,
  //             },
  //             ...(targetKey && {
  //               [targetKey]: base[targetKey],
  //             }),
  //           };
  //         }

  //         if (groupKey === "tab") {
  //           return { ...prev, [key]: value };
  //         }

  //         return {
  //           ...prev,
  //           [groupKey]: { ...prev[groupKey], [key]: value },
  //         };
  //       });
  //     } else if (typeOfMode === "view") {
  //       setState((prev) =>
  //         groupKey === "tab" ? { ...prev, [key]: value } : prev,
  //       );
  //     }
  //   },
  //   [typeOfMode],
  // );

  const updateState = useCallback(
    (groupKey, key, value) => {
      if (!activeId) return; // optional safety

      if (typeOfMode === "create" || typeOfMode === "edit") {
        if (groupKey === "parameterState" && key === "parameter") {
          setConfigSetIds({
            control_limit_options: value.control_limit_options || [],
            specification_limit_options:
              value.specification_limit_options || [],
          });
        }

        setCharts((prevCharts) =>
          prevCharts.map((chart) => {
            if (chart?.id !== activeId) return chart;

            const base = initialState();
            const prev = chart;

            if (groupKey === "parameterState" && key === "siteUnitParameter") {
              return {
                ...chart,
                // configState: {
                //   ...prev,
                parameterState: {
                  ...activeChart?.parameterState,
                  ...value,
                  parameter: {},
                },
                // },
              };
            }

            if (groupKey === "parameterState" && key === "parameter") {
              return {
                ...chart,
                // configState: {
                //   ...prev,
                parameterState: {
                  ...prev.parameterState,
                  [key]: value,
                },
                // },
              };
            }

            if (groupKey === "parameterState" && key === "plotType") {
              const targetKey = chartConfigMap[value]?.key;

              return {
                ...chart,
                // configState: {
                // ...prev,
                parameterState: {
                  ...prev.parameterState,
                  plotType: value,
                },
                ...(targetKey && {
                  [targetKey]: base[targetKey],
                }),
                // },
              };
            }

            if (groupKey === "tab") {
              return {
                ...chart,
                // configState: {
                //   ...prev,
                [key]: value,
                // },
              };
            }

            return {
              ...chart,
              // configState: {
              //   ...prev,
              [groupKey]: {
                ...prev[groupKey],
                [key]: value,
              },
              // },
            };
          }),
        );
      } else if (typeOfMode === "view") {
        setCharts((prevCharts) =>
          prevCharts.map((chart) => {
            if (chart?.id !== activeId) return chart;
            if (groupKey !== "tab") return chart;

            return {
              ...chart,
              // configState: {
              //   ...chart.configState,
              [key]: value,
              // },
            };
          }),
        );
      }
    },
    [typeOfMode, activeId], // ✅ FIX
  );

  const onClickClearAllFilters = useCallback(() => {
    if (typeOfMode === "create" || typeOfMode === "edit") {
      // setState(initialState());
      setCharts((prev) =>
        prev.map((chart) => {
          return chart?.id === activeId
            ? {
                // ...chart,
                // // configState: {}, // reset config
                // batchState: {}, // reset batch state
                // // chartConfig: {}, // reset chart config

                id: chart?.id,
                name: chart.name,
                selected: chart.selected,
                isEditing: chart.isEditing,
                // configState: {
                //   batchState: {},
                //   chartConfig: {},
                // },
                batchState: initialState().batchState,
                parameterState: {
                  ...initialState().parameterState,
                  plotType: "",
                },
                // chartConfig: {},
              }
            : chart;
        }),
      );
      setPreviewData([]);
      setShowPreview(false);
      dispatch(clearPlot());
    }
  }, [charts, dispatch, typeOfMode]);

  const updateChartData = (chartId, data, error = null) => {
    if (data === null) {
      setCharts((prev) => {
        console.log(prev);
        return prev.map((chart) => {
          if (chart?.id !== chartId) return chart;

          return {
            ...chart,
            selected: true,
            isError: true,
            errorMessage: error || "Something went wrong",
            parsedData: {},
            subPlotsData: {},
            histogramData: {},
            tableData: [],
            violationTableData: [],
            rawData: [],
            summaryTableTitles: "",
            violationTableTitles: "",
            uniqueBatches: [],
          };
        });
      });
      return;
    } else {
      setCharts((prev) =>
        prev.map((chart) => {
          if (chart?.id !== chartId) return chart;

          if (error) {
            return {
              ...chart,
              selected: true,
              isError: true,
              errorMessage: error,
              parsedData: {},
              subPlotsData: {},
              histogramData: {},
              tableData: [],
              violationTableData: [],
            };
          }

          const parsedPlot =
            typeof data?.plot_data === "string" && data.plot_data.trim() !== ""
              ? JSON.parse(data.plot_data)
              : {};

          const updatedChart = {
            ...chart,
            selected: true,
            isError: false,
            errorMessage: null,
            parsedData: parsedPlot,
            subPlotsData: data?.subplots || {},
            histogramData: data?.histogram || {},
            tableData: data?.summary_table || [],
            violationTableData: data?.voilation_table || [],
            titles: data?.titles || {},
            chartDataToSave: data,
            summaryTableTitles: data?.summary_table_headers || "",
            violationTableTitles: data?.vialation_table_headers || "",
            uniqueBatches: data?.unique_batches || [],
          };

          if (data?.info) {
            updatedChart.configBatchList =
              data?.info?.available_batch_ids || [];
            // updatedChart.configSetIds = {
            //   control_limit_options: data?.info?.control_limit_options || [],
            //   specification_limit_options:
            //     data?.info?.specification_limit_options || [],
            // };
            updatedChart.rawData = data?.info?.raw_data || [];
          }

          if (chart.parameterState?.plotType === "xbar_chart") {
            updatedChart.xBarChartConfigState = {
              ...chart.xBarChartConfigState,
              selectedBatchId: data?.info?.current_batch_id,
            };
          }

          return updatedChart;
        }),
      );
    }
  };

  const extractErrorMessage = (err) => {
    try {
      // Case 1: your custom backend format
      if (err?.body?.[0]) {
        const parsed = JSON.parse(err.body[0]);
        return parsed?.message || parsed?.error;
      }

      // Case 2: axios standard
      if (err?.response?.data?.message) {
        return err.response.data.message;
      }

      return "Something went wrong";
    } catch (e) {
      return "Something went wrong";
    }
  };

  const handleGenerate = useCallback(async () => {
    const activeChart = charts?.find((c) => c?.id === activeId);
    const chartTypeLocal = activeChart?.parameterState?.plotType || "";
    const batchState = activeChart?.batchState || {};
    const parameterState = activeChart?.parameterState;

    // const config = activeChart?.chartConfig || {};
    let config = null;

    if (chartTypeLocal === "control_chart")
      config = activeChart.controlChartConfigState;
    else if (chartTypeLocal === "process_capability")
      config = activeChart.pccChartConfigState;
    else if (chartTypeLocal === "xbar_chart")
      config = activeChart.xBarChartConfigState;
    else if (chartTypeLocal === "pp_chart")
      config = activeChart.ppChartConfigState;

    const formatDatesLocal = (value) => {
      if (!value) return value;
      const date = new Date(value);
      if (isNaN(date)) return value;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    console.log("Generating with batchState:", activeChart);
    const batchDataPayload = {
      ...batchState,
      plotType: parameterState?.plotType,
      parameterId: parameterState?.parameter?.parameterId,
      startDate: formatDatesLocal(batchState?.startDate),
      endDate: formatDatesLocal(batchState?.endDate),
    };
    delete batchDataPayload.parameter;

    const payload = {
      drug_product_id: parameterState?.product?.id,
      site_id: parameterState?.site?.id,
      unit_op_id: parameterState?.unitOfOperation?.id,
      batchs_selection: toSnakeCase(batchDataPayload),
      viz_config: toSnakeCase(config),
      author_id: localStorage.getItem("userid"),
    };

    try {
      const res = await dispatch(fetchPlot(payload));
      if (!res || res?.error || res?.status?.[0] >= 400) {
        const msg =
          res?.message || extractErrorMessage(res) || "Something went wrong";

        updateChartData(activeId, null, msg);
        setShowConfigPanel(false);
        return;
      }

      // setPlotKey((p) => p + 1);
      updateChartData(activeId, res);
      setShowConfigPanel(false);
    } catch (err) {
      const msg = extractErrorMessage(err);

      updateChartData(activeId, null, msg);

      setSnackbar({
        open: true,
        severity: "error",
        message: msg,
      });
    }
  }, [dispatch, product?.id, site?.id, unitOfOperation?.id, charts, activeId]);

  // const handleClearCurrentConfigState = useCallback(() => {
  //   if (typeOfMode === "create" || typeOfMode === "edit") {
  //     const reset = initialState();

  //     // Use chartConfigMap to determine the correct config state key
  //     const chartConfigStateKey =
  //       chartConfigMap[state.batchState.plotType]?.key;

  //     if (chartConfigStateKey && reset[chartConfigStateKey] !== undefined) {
  //       setState((prev) => ({
  //         ...prev,
  //         [chartConfigStateKey]: reset[chartConfigStateKey],
  //       }));
  //     }
  //   }
  // }, [state.batchState.plotType, typeOfMode]);

  const onClickApply = useCallback(
    (ids, closePreview) => {
      if (typeOfMode === "create" || typeOfMode === "edit") {
        setCharts((prev) =>
          prev.map((chart) => {
            return chart?.id === activeId
              ? {
                  ...chart,
                  batchState: {
                    ...chart.batchState,
                    batchIdList: ids,
                  },
                }
              : chart;
          }),
        );

        setPreviewData((prevData) =>
          prevData.map((batch) => ({
            ...batch,
            isSelected: ids.includes(batch.batch_id),
          })),
        );

        if (closePreview) setShowPreview(false);
      }
    },
    [activeId, typeOfMode],
  );

  const handleClearCurrentConfigState = useCallback(() => {
    if (typeOfMode !== "create" && typeOfMode !== "edit") return;

    setCharts((prevCharts) =>
      prevCharts.map((chart) => {
        if (chart?.id !== activeId) return chart;

        const plotType = chart.parameterState?.plotType;

        // const newConfig = chartConfigMap[plotType]?.getInitial?.() || {};
        const targetKey = chartConfigMap[plotType]?.key;
        return {
          ...chart,
          // configState: {
          //   ...chart.configState,
          // chartConfig: newConfig, // reset ONLY current config
          // },
          // batchState: {},
          // chartConfig: newConfig,

          id: chart?.id,
          name: chart.name,
          selected: chart.selected,
          isEditing: chart.isEditing,
          // configState: {
          //   batchState: {},
          //   chartConfig: {},
          // },
          batchState: initialState().batchState,
          [targetKey]: initialState()[targetKey],
          // chartConfig: {},
        };
      }),
    );
  }, [activeId, typeOfMode]);

  const handleSaveChart = useCallback(
    async (dataToSave) => {
      if (!canSaveChart) return;
      setpageLoader(true);

      const formatDatesLocal = (value) => {
        if (!value) return value;
        const date = new Date(value);
        if (isNaN(date)) return value;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const payload = {
        viz_id: viz_id,
        // version_id: version_id,
        user_id: localStorage.userid,
        viz_saved_name: dataToSave.name,
        description: dataToSave.description,
        data_embedded: dataToSave.dataEmbedded,
        user_name: localStorage.getItem("userName"),
        data: null,
      };

      const data = [];
      for (const chart of charts) {
        const chartType = chart.parameterState.plotType;
        let config = null;
        if (chartType === "control_chart")
          config = chart.controlChartConfigState;
        else if (chartType === "process_capability")
          config = chart.pccChartConfigState;
        else if (chartType === "xbar_chart")
          config = chart.xBarChartConfigState;
        else if (chartType === "pp_chart") config = chart.ppChartConfigState;

        // const activeChart = charts.find((c) => c.id === activeId);
        // const batchState = activeChart?.batchState || {};
        // const chartConfigState = activeChart?.chartConfig || {};

        data.push({
          chart_name: chart.name,
          selected: chart.selected,
          drug_product_id: chart.parameterState?.product?.id,
          site_id: chart.parameterState?.site?.id,
          unit_op_id: chart.parameterState?.unitOfOperation?.id,
          parameter_id: chart.parameterState?.parameter?.parameterId,
          // user_id: localStorage.userid,
          chart_uuid: chart.chart_uuid,

          payload_data: {
            batchs_selection: toSnakeCase({
              ...chart.batchState,
              plotType: chart.parameterState?.plotType,
              parameter: chart.parameterState?.parameter,
              parameterId: chart.parameterState?.parameter?.parameterId,
              startDate: formatDatesLocal(chart.batchState.startDate),
              endDate: formatDatesLocal(chart.batchState.endDate),
            }),
            viz_config: toSnakeCase(config),
          },
          viz_data: chart.chartDataToSave,
        });
      }

      payload.data = data;

      try {
        await dispatch(saveVisualization(payload));
        setSaveSnackbar({
          open: true,
          severity: "success",
          message: "Chart saved successfully!",
        });
        setTimeout(() => navigate("/analytics/visualization"), 1500);
      } catch (err) {
        setSaveSnackbar({
          open: true,
          severity: "error",
          message: err?.response?.data?.details || "Failed to save chart",
        });
      } finally {
        setpageLoader(false);
      }
    },
    [
      canSaveChart,
      charts,
      dispatch,
      navigate,
      product?.id,
      site?.id,
      unitOfOperation?.id,
    ],
  );

  const handleBack = useCallback(() => navigate(-1), [navigate]);

  const formatDatesLocal = (value) => {
    if (!value) return value; // keep null/empty as-is
    const date = new Date(value);
    if (isNaN(date)) return value; // not a valid date, return as-is

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-indexed
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`; // YYYY-MM-DD in local time
  };

  const onClickPreview = async () => {
    if (typeOfMode === "create" || typeOfMode === "edit") {
      // const activeChart = charts.find((c) => c.id === activeId);
      // const batchState = activeChart?.batchState || {};

      const payload = {
        drug_product_id: product?.id,
        site_id: site?.id,
        unit_op_id: unitOfOperation?.id,
        batchs_selection: {
          batch_range_option: batchState?.batchRangeOption,
          recent_batch_count: batchState?.recentBatchCount,
          recent_day_count: batchState?.recentDayCount,
          start_date: formatDatesLocal(batchState?.startDate),
          end_date: formatDatesLocal(batchState?.endDate),
          // batch_id_list: batchState?.batchIdList,
        },
      };

      // const newPayloadStr = JSON.stringify(payload);
      // const lastPayloadStr = JSON.stringify(lastPreviewPayload.current);

      // if (newPayloadStr !== lastPayloadStr) {
      const response = await dispatch(fetchFilteredBatchList(payload));
      if (response) {
        // setPreviewData(response.map((b) => ({ ...b, isSelected: true })));
        const selectedIds = batchState?.batchIdList || [];

        setPreviewData(
          response.map((b) => ({
            ...b,
            isSelected:
              typeOfMode === "edit" ? selectedIds.includes(b.batch_id) : true,
          })),
        );
      }
      lastPreviewPayload.current = payload; // update ref
      // }
    }
    setShowPreview(true);
  };

  const memoizedHistogramData = useMemo(() => histogramData, [histogramData]);

  // const activeChart = useMemo(
  //   () => charts.find((c) => c.id === activeId),
  //   [charts, activeId],
  // );

  const handleAddChart = () => {
    if (!canCreateChart) return;
    const id = Date.now().toString();

    const nextIndex = charts?.length + 1;
    setCharts((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        name: `Chart ${nextIndex}`,
        selected: false,
        isEditing: false,
        // configState: {
        //   batchState: {},
        //   chartConfig: {},
        // },
        batchState: initialState().batchState,
        parameterState: initialState().parameterState,
        // chartConfig: {},
      },
    ]);

    setActiveId(id);
    setShowConfigPanel(false);
    setShowPreview(false);
  };

  const handleRefresh = (type) => {
    dispatch(clearPlot());
    fetchData("refresh", type);
  };

  const firstChartIdToShowSave = charts.find((c) => c.selected !== false)?.id;

  const isSaveDisabled = useMemo(() => {
    if (!charts || charts.length === 0) return true;

    return charts.some((chart) => {
      const data = chart?.chartDataToSave;

      // ❗ adjust based on actual API shape
      const hasPlotData =
        data &&
        data.plot_data &&
        typeof data.plot_data === "string" &&
        data.plot_data.trim() !== "";

      const hasInfo = data && data.info && Object.keys(data.info).length > 0;

      return !(hasPlotData && hasInfo);
    });
  }, [charts]);

  // Everything the Charts page needs to fully restore itself once the user
  // comes back from Manage Data via the "Back to Chart" action there.
  const handleBatchIdClick = (batchId) => {
    const chartsPageSnapshot = {
      charts,
      activeId,
      selectedTab,
      showConfigPanel,
      showSummaryTable,
      showViolationTable,
      previewData,
      showPreview,
      configSetIds,
      disableAllFields,
    };

    const restoredStateForReturn = {
      ...location.state,
      restoredChartsPageState: chartsPageSnapshot,
    };

    // Update THIS history entry in place first, so that if the user presses
    // the browser's native Back button later (instead of our "Back to Chart"
    // button), landing back here restores the same snapshot automatically.
    navigate(location.pathname + location.search, {
      replace: true,
      state: restoredStateForReturn,
    });

    dispatch(
      setProductSitesUopFilters({
        product: product,
        site: site,
        unitOfOperation: unitOfOperation,
      }),
    );

    // Now push Manage Data on top of that updated entry.
    navigate(`/${manageDataRoute.reviewBatches}`, {
      state: {
        batchId,
        unitOfOperation,
        site,
        product,
        // Manage Data uses this to know exactly where + how to send the
        // user back: same URL, same full previous router state.
        returnTo: {
          pathname: location.pathname,
          search: location.search,
          state: restoredStateForReturn,
        },
      },
    });
  };

  return (
    <>
      <CAMHeader />
      <CAMDrawer />
      <InvestigatorContainer>
        {/* <DropdownsBar /> */}
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={10}>
            <TabsBar />
          </Grid>
        </Grid>

        <Box>
          <Divider sx={{ mx: 2 }} />

          <Box
            sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
            px={2}
            // py={0.5}
          >
            <Chip
              icon={
                <ArrowBackIcon
                // style={{ color: "#181D27", fontSize: 16, marginRight: 4 }}
                />
              }
              label={
                "Back"
                // <Label text="Back" color="#181D27" />
              }
              onClick={handleBack}
              clickable
              size="small"
              variant="filled"
              sx={{ padding: "0px 8px", fontSize: "12px" }}
              // sx={{
              //   height: 34,
              //   fontSize: 12,
              //   padding: "0 12px",
              //   "& .MuiChip-label": {
              //     padding: 0.5,
              //     display: "flex",
              //     alignItems: "center",
              //   },
              // }}
            />

            <BreadcrumbsBar />
          </Box>

          <Divider sx={{ mx: 2 }} />
        </Box>

        <Box className="visualization-container">
          <Grid container className="grid-container">
            <Grid item xs={2.7} className="side-panel" ref={leftRef}>
              <Box className="side-card-wrapper">
                <Box className="scrollable-panel">
                  <CreateChartsPanel
                    setCharts={setCharts}
                    charts={charts}
                    setAnchorEl={setAnchorEl}
                    anchorEl={anchorEl}
                    activeId={activeId}
                    setActiveId={setActiveId}
                    handleAddChart={handleAddChart}
                    setShowConfigPanel={setShowConfigPanel}
                    setSelectedTab={setSelectedTab}
                    saving={pageLoader}
                    setSaveDialogOpen={setSaveDialogOpen}
                    disableAllFields={disableAllFields}
                    showSummaryTable={showSummaryTable}
                    setShowSummaryTable={setShowSummaryTable}
                    showViolationTable={showViolationTable}
                    setShowViolationTable={setShowViolationTable}
                    saveDisabled={isSaveDisabled}
                    setShowPreview={setShowPreview}
                    typeOfMode={typeOfMode}
                    vizName={vizName}
                    canCreateChart={canCreateChart}
                    canEditChart={canEditChart}
                    canDeleteChart={canDeleteChart}
                    canSaveChart={canSaveChart}
                    handleRefresh={handleRefresh}
                  />
                </Box>
              </Box>
            </Grid>

            <Grid
              item
              // xs={showPreview ? 6.3 : 9.3}
              xs={9.3}
              className="chart-area"
            >
              {loader || pageLoader ? (
                <Box className="loading-container">
                  <CircularProgress />
                </Box>
              ) : // : error ? (
              //   <Box className="error-message">
              //     <Label text={message || "Error loading chart"} color="red" />
              //   </Box>
              // ) : // ) : success && data ? (
              charts?.length > 0 ? (
                charts?.map(
                  (chart, index) =>
                    chart.selected && (
                      <Box key={chart.id}>
                        <Box sx={{ width: "100%", marginBottom: 1 }}>
                          <VisualizationTableSubChartAndOtherData
                            index={index}
                            key={chart?.id}
                            typeOfMode={typeOfMode}
                            batchState={chart?.batchState}
                            activeChart={chart}
                            currentChartConfigState={
                              chart[
                                chart.parameterState?.plotType ===
                                "control_chart"
                                  ? "controlChartConfigState"
                                  : chart.parameterState?.plotType ===
                                      "process_capability"
                                    ? "pccChartConfigState"
                                    : chart.parameterState?.plotType ===
                                        "xbar_chart"
                                      ? "xBarChartConfigState"
                                      : chart.parameterState?.plotType ===
                                          "pp_chart"
                                        ? "ppChartConfigState"
                                        : ""
                              ]
                            }
                            product={product}
                            site={site}
                            unitOfOperation={unitOfOperation}
                            setSaveDialogOpen={setSaveDialogOpen}
                            saving={pageLoader}
                            parameterState={parameterState}
                            firstChartIdToShowSave={firstChartIdToShowSave}
                            showSummaryTable={showSummaryTable}
                            showViolationTable={showViolationTable}
                            handleBatchIdClick={handleBatchIdClick}
                            isRestoredStateForReturn={restoredState}
                          />

                          {chart.isError && (
                            <Box className="error-message" sx={{ mt: 1 }}>
                              <Label
                                text={
                                  chart.errorMessage || "Error loading chart"
                                }
                                color="red"
                              />
                            </Box>
                          )}
                        </Box>

                        {charts?.length !== index + 1 && (
                          <Divider sx={{ my: 2 }} />
                        )}
                      </Box>
                    ),
                )
              ) : (
                <Box className="chart-placeholder">
                  <Label text="Chart will be rendered here" color="#888" />
                </Box>
              )}
            </Grid>
          </Grid>
          {showConfigPanel && (
            <Grid container className="config-overlay-grid">
              <Grid item xs={2.7} />{" "}
              {/* Empty spacer = align with left panel */}
              <Grid item xs={3.6}>
                <Box className="config-overlay-panel">
                  <Card elevation={3} className="overlay-card">
                    {/* YOUR EXISTING TAB + CONFIG CONTENT HERE */}
                    <Box className="scrollable-panel">
                      <Box item className="side-panel">
                        <Box className="side-card-wrapper">
                          <Card
                            elevation={0}
                            className="filter-card"
                            sx={{ mt: 0, mb: 1 }}
                          >
                            <CardContent className="tab-header">
                              <Box
                                className="tab-buttons-container"
                                sx={{ gap: 1 }}
                              >
                                <Button
                                  size="small"
                                  variant={
                                    selectedTab === "parameter"
                                      ? "contained"
                                      : "text"
                                  }
                                  onClick={() => setSelectedTab("parameter")}
                                  className="tab-button"
                                  sx={{
                                    minWidth: 90,
                                    px: 1.5,
                                    py: 0.5,
                                    fontSize: 12,
                                  }}
                                >
                                  <Label
                                    text="Parameter"
                                    color={
                                      selectedTab === "parameter" && "white"
                                    }
                                  />
                                </Button>

                                <Button
                                  size="small"
                                  variant={
                                    selectedTab === "batchFilters"
                                      ? "contained"
                                      : "text"
                                  }
                                  onClick={() => setSelectedTab("batchFilters")}
                                  className="tab-button"
                                  sx={{
                                    minWidth: 90,
                                    px: 1.5,
                                    py: 0.5,
                                    fontSize: 12,
                                  }}
                                >
                                  <Label
                                    text="Batch Filters"
                                    color={
                                      selectedTab === "batchFilters" && "white"
                                    }
                                  />
                                </Button>

                                <Button
                                  size="small"
                                  variant={
                                    selectedTab === "configuration"
                                      ? "contained"
                                      : "text"
                                  }
                                  onClick={() =>
                                    setSelectedTab("configuration")
                                  }
                                  className="tab-button"
                                  sx={{
                                    minWidth: 100,
                                    px: 1.5,
                                    py: 0.5,
                                    fontSize: 12,
                                  }}
                                >
                                  <Label
                                    text="Configuration"
                                    color={
                                      selectedTab === "configuration" && "white"
                                    }
                                  />
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>

                          <Box className="scrollable-panel">
                            {selectedTab === "parameter" ? (
                              <ParameterFilterPanel
                                drugProductId={product?.id}
                                siteId={site?.id}
                                unitOfOperationId={unitOfOperation?.id}
                                updateState={(key, value) =>
                                  updateState("parameterState", key, value)
                                }
                                // setShowPreview={setShowPreview}
                                onClickClearAllFilters={onClickClearAllFilters}
                                handleGenerate={handleGenerate}
                                batchState={batchState}
                                disableAllFields={disableAllFields}
                                setPreviewData={setPreviewData}
                                // onClickPreview={onClickPreview}
                                setShowConfigPanel={setShowConfigPanel}
                                parameterState={parameterState}
                                setSelectedTab={setSelectedTab}
                              />
                            ) : !parameterState?.plotType ? (
                              <Box className="empty-config-message">
                                <Label
                                  text="Please select a Plot Type in the Parameter section to configure other chart settings."
                                  fontSize="16px"
                                  fontWeight="500"
                                  color="#757575"
                                />
                              </Box>
                            ) : selectedTab === "batchFilters" ? (
                              <BatchFilterPanel
                                drugProductId={product?.id}
                                siteId={site?.id}
                                unitOfOperationId={unitOfOperation?.id}
                                updateState={(key, value) =>
                                  updateState("batchState", key, value)
                                }
                                setShowPreview={setShowPreview}
                                onClickClearAllFilters={onClickClearAllFilters}
                                handleGenerate={handleGenerate}
                                batchState={batchState}
                                disableAllFields={disableAllFields}
                                setPreviewData={setPreviewData}
                                onClickPreview={onClickPreview}
                                setShowConfigPanel={setShowConfigPanel}
                                parameterState={parameterState}
                                setSelectedTab={setSelectedTab}
                              />
                            ) : ConfigComponent ? (
                              <ConfigComponent
                                unitOfOperationId={unitOfOperation?.id}
                                updateState={(key, value) =>
                                  updateState(currentChartKey, key, value)
                                }
                                ruleOptions={ruleOptions}
                                handleGenerate={handleGenerate}
                                configState={currentChartConfigState}
                                onClickClearCurrentConfig={
                                  handleClearCurrentConfigState
                                }
                                configBatchList={configBatchList}
                                configSetIds={configSetIds}
                                disableAllFields={disableAllFields}
                                setShowConfigPanel={setShowConfigPanel}
                                setSelectedTab={setSelectedTab}
                              />
                            ) : null}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Card>
                </Box>
              </Grid>
              {showPreview && (
                <Grid item xs={3} className="batch-preview-grid-panel">
                  <BatchPreview
                    showPreview={showPreview}
                    setShowPreview={setShowPreview}
                    previewData={previewData}
                    setPreviewData={setPreviewData}
                    onClickApply={onClickApply}
                    disableAllFields={disableAllFields}
                    batchLoader={batchLoader}
                  />
                </Grid>
              )}
            </Grid>
          )}
        </Box>
      </InvestigatorContainer>

      <SaveChartDialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        onSave={(data) => handleSaveChart(data)}
        // chartState={charts}
        // chartData={data}
      />

      {/* <Snackbar
        open={!!message}
        autoHideDuration={3000}
        onClose={() => {
          setSnackbar((s) => ({ ...s, open: false }));
          // dispatch(clearPlot());
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={error ? "error" : "success"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar> */}

      <Snackbar
        open={saveSnackbar.open}
        autoHideDuration={3000}
        onClose={() => setSaveSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={saveSnackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {saveSnackbar.message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* <Box display="flex" justifyContent="center" width="100%">
        <ProgressBar />
      </Box> */}
    </>
  );
};

export default VisualizationPage;
